import { Check, FileText, Sparkles } from "lucide-react";

export type ExtractionStatus =
  | "uploading"
  | "scanning"
  | "identifying"
  | "extracting"
  | "mapping"
  | "complete";

const STEPS: Array<{ key: string; label: string; statuses: ExtractionStatus[] }> = [
  { key: "reading", label: "Reading your file", statuses: ["uploading", "scanning"] },
  { key: "identifying", label: "Matching the service", statuses: ["identifying"] },
  { key: "extracting", label: "Extracting request details", statuses: ["extracting"] },
  { key: "mapping", label: "Mapping to the request form", statuses: ["mapping"] },
];

type LaneState = "pending" | "active" | "complete";

function laneState(index: number, status: ExtractionStatus): LaneState {
  if (status === "complete") return "complete";
  const activeIndex = STEPS.findIndex((s) => s.statuses.includes(status));
  if (activeIndex === -1) return "pending";
  if (index < activeIndex) return "complete";
  if (index === activeIndex) return "active";
  return "pending";
}

type Props = {
  status: ExtractionStatus;
  fileName: string;
};

export function RequestExtractionProgressCard({ status, fileName }: Props) {
  return (
    <div
      className="card rounded-4 shadow-sm m-3"
      style={{ border: "1px solid var(--color-brand-teal-100, #b3ddd3)" }}
    >
      <div
        className="card-title rounded-top-4 px-3 py-2 gap-2 border-bottom d-flex align-items-center m-0"
        style={{
          backgroundColor: "var(--color-brand-teal-50, #e6f5f1)",
          color: "var(--color-brand-teal-800, #1a5a4f)",
          fontSize: "var(--font-size-xs)",
        }}
      >
        <Sparkles size={16} strokeWidth={2} aria-hidden />
        Start from a file
      </div>

      <div className="card-body d-flex flex-column gap-3 p-3">
        <div className="d-flex align-items-center gap-2 text-truncate">
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: 32,
              height: 32,
              backgroundColor: "var(--color-brand-teal-50, #e6f5f1)",
              color: "var(--color-brand-primary-blue-80)",
              flexShrink: 0,
            }}
            aria-hidden
          >
            <FileText size={16} />
          </span>
          <span className="text-truncate fw-medium">{fileName}</span>
        </div>

        {STEPS.map((step, index) => {
          const state = laneState(index, status);
          return (
            <div key={step.key} className="d-flex flex-column gap-1">
              <div className="d-flex align-items-center justify-content-between">
                <span
                  className={`fw-semibold ${state === "pending" ? "text-muted" : ""}`}
                  style={{ fontSize: "var(--font-size-sm)" }}
                >
                  {step.label}
                </span>
                {state === "complete" && (
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      width: 20,
                      height: 20,
                      backgroundColor: "var(--color-brand-primary-aqua-100, #138ea0)",
                      color: "white",
                    }}
                    aria-hidden
                  >
                    <Check size={12} strokeWidth={3} />
                  </span>
                )}
                {state === "active" && (
                  <span
                    className="rounded-circle"
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: "var(--color-brand-primary-aqua-100, #138ea0)",
                    }}
                    aria-hidden
                  />
                )}
              </div>
              <div
                className="rounded-pill"
                style={{
                  height: 4,
                  backgroundColor: "var(--color-neutral-300, #ebebeb)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width:
                      state === "complete"
                        ? "100%"
                        : state === "active"
                          ? "60%"
                          : "0%",
                    backgroundColor: "var(--color-brand-primary-aqua-100, #138ea0)",
                    transition: "width 300ms ease-out",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
